import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { X, Upload, FileText, Package, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";

import { ticketService } from "@features/tickets/services/ticketService";
import { useAuth } from "@features/auth/contexts/AuthContext";

const createTicketSchema = z.object({
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title cannot exceed 200 characters"),

  description: z
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(2000, "Description cannot exceed 2000 characters"),

  category: z.enum([
    "pickup_issue",
    "delivery_issue",
    "package_damage",
    "payment_issue",
    "system_issue",
    "general_inquiry",
    "complaint",
    "feature_request",
    "other",
  ]),

  relatedPackage: z.string().optional(),
  relatedPickup: z.string().optional(),

  tags: z.array(z.string()).optional(),
});

const CreateTicketDialog = ({
  open,
  onClose,
  onSuccess,
  packageData = null,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      title: packageData
        ? `Issue with Package ${packageData.trackingNumber}`
        : "",
      description: packageData
        ? `Package Details:\n- Tracking Number: ${
            packageData.trackingNumber
          }\n- Package ID: ${packageData.packageId}\n- Current Status: ${
            packageData.status
          }\n- Customer: ${
            packageData.customer?.name || "N/A"
          }\n\nIssue Description:\n`
        : "",
      category: packageData ? "delivery_issue" : "general_inquiry",
      relatedPackage: packageData ? packageData.packageId : "",
      relatedPickup: "",
      tags: [],
    },
  });

  // Reset form when dialog opens with package data
  React.useEffect(() => {
    if (open && packageData) {
      form.reset({
        title: `Issue with Package ${packageData.trackingNumber}`,
        description: `Package Details:\n- Tracking Number: ${
          packageData.trackingNumber
        }\n- Package ID: ${packageData.packageId}\n- Current Status: ${
          packageData.status
        }\n- Customer: ${
          packageData.customer?.name || "N/A"
        }\n\nIssue Description:\n`,
        category: "delivery_issue",
        relatedPackage: packageData.packageId,
        relatedPickup: "",
        tags: [],
      });
    }
  }, [open, packageData, form]);

  // Get user's packages for dropdown (optional)
  const { data: userPackages } = useQuery({
    queryKey: ["userPackages"],
    queryFn: () => {
      // This would need to be implemented to get user's packages
      // For now, return empty array
      return Promise.resolve([]);
    },
    enabled: open && user?.role === "client",
  });

  const createTicketMutation = useMutation({
    mutationFn: (data) => ticketService.createTicket(data),
    onSuccess: (response) => {
      toast.success(t("tickets.createSuccess", "Ticket created successfully"));
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("tickets.createError", "Failed to create ticket")
      );
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(
          t(
            "tickets.fileTooLarge",
            "File {{fileName}} is too large (max 10MB)",
            { fileName: file.name }
          )
        );
        return false;
      }
      return true;
    });

    // Limit to 5 files total
    const newAttachments = [...attachments, ...validFiles];
    if (newAttachments.length > 5) {
      toast.error(t("tickets.tooManyFiles", "Maximum 5 files allowed"));
      setAttachments(newAttachments.slice(0, 5));
    } else {
      setAttachments(newAttachments);
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const ticketData = {
      ...data,
      attachments,
    };

    // Remove empty fields
    if (!ticketData.relatedPackage) delete ticketData.relatedPackage;
    if (!ticketData.relatedPickup) delete ticketData.relatedPickup;
    if (!ticketData.tags || ticketData.tags.length === 0)
      delete ticketData.tags;

    createTicketMutation.mutate(ticketData);
  };

  const handleClose = () => {
    form.reset();
    setAttachments([]);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div>{t("tickets.createTicket", "Create Support Ticket")}</div>
              <p className="text-sm font-normal text-muted-foreground mt-1">
                {t(
                  "tickets.createTicketSubtitle",
                  "We're here to help resolve your issue"
                )}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Enhanced Package Info Badge */}
        {packageData && (
          <div className="relative bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-md bg-blue-600 dark:bg-blue-500">
                <Package className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                {t("tickets.relatedPackageInfo", "Related Package")}
              </span>
              <Badge variant="secondary" className="ml-auto">
                {t("tickets.autoFilled", "Auto-filled")}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 bg-white/50 dark:bg-black/10 rounded-md p-2">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  {t("packages.trackingNumber", "Tracking #")}:
                </span>
                <Badge variant="secondary" className="font-mono">
                  {packageData.trackingNumber}
                </Badge>
              </div>
              <div className="flex items-center gap-2 bg-white/50 dark:bg-black/10 rounded-md p-2">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  {t("common.status", "Status")}:
                </span>
                <Badge variant="outline" className="font-medium">
                  {packageData.status}
                </Badge>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tickets.title", "Title")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        "tickets.titlePlaceholder",
                        "Enter a brief title for your issue"
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.category", "Category")}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "tickets.selectCategory",
                            "Select category"
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pickup_issue">
                        {t("tickets.category.pickup_issue", "Pickup Issue")}
                      </SelectItem>
                      <SelectItem value="delivery_issue">
                        {t("tickets.category.delivery_issue", "Delivery Issue")}
                      </SelectItem>
                      <SelectItem value="package_damage">
                        {t("tickets.category.package_damage", "Package Damage")}
                      </SelectItem>
                      <SelectItem value="payment_issue">
                        {t("tickets.category.payment_issue", "Payment Issue")}
                      </SelectItem>
                      <SelectItem value="system_issue">
                        {t("tickets.category.system_issue", "System Issue")}
                      </SelectItem>
                      <SelectItem value="general_inquiry">
                        {t(
                          "tickets.category.general_inquiry",
                          "General Inquiry"
                        )}
                      </SelectItem>
                      <SelectItem value="complaint">
                        {t("tickets.category.complaint", "Complaint")}
                      </SelectItem>
                      <SelectItem value="feature_request">
                        {t(
                          "tickets.category.feature_request",
                          "Feature Request"
                        )}
                      </SelectItem>
                      <SelectItem value="other">
                        {t("tickets.category.other", "Other")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("tickets.description", "Description")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        "tickets.descriptionPlaceholder",
                        "Describe your issue in detail..."
                      )}
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Related Package/Pickup */}
            {user?.role === "client" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="relatedPackage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "tickets.relatedPackage",
                          "Related Package (Optional)"
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            "tickets.packageIdPlaceholder",
                            "Package ID or Tracking Number"
                          )}
                          {...field}
                          readOnly={!!packageData}
                          disabled={!!packageData}
                          className={
                            packageData
                              ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                              : ""
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="relatedPickup"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "tickets.relatedPickup",
                          "Related Pickup (Optional)"
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            "tickets.pickupIdPlaceholder",
                            "Pickup ID"
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* File Attachments */}
            <div className="space-y-3">
              <FormLabel>
                {t("tickets.attachments", "Attachments (Optional)")}
              </FormLabel>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        {t("tickets.uploadFiles", "Upload files")}
                      </span>
                      <span className="mt-1 block text-xs text-gray-500">
                        {t(
                          "tickets.fileRequirements",
                          "PNG, JPG, PDF up to 10MB (max 5 files)"
                        )}
                      </span>
                    </label>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      multiple
                      accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>

              {/* Selected Files */}
              {attachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    {t("tickets.selectedFiles", "Selected Files")} (
                    {attachments.length}/5)
                  </p>
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {formatFileSize(file.size)}
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                {t(
                  "tickets.responseTime",
                  "We typically respond within 24 hours"
                )}
              </p>
              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none"
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      {t("tickets.creating", "Creating...")}
                    </>
                  ) : (
                    <>
                      <MessageSquarePlus className="h-4 w-4" />
                      {t("tickets.createTicket", "Create Ticket")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTicketDialog;
